import React, { useState, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { USER_ROLES } from 'utils/userRoles'
import Modal from 'components/atoms/Modal'
import SearchableSelect from 'components/SearchableSelect'
import Avatar from 'components/Avatar'
import { useDispatch, useSelector } from 'react-redux'
import { removeAccessToBpRoutine, restoreAccessToBpRoutine } from 'features/accessControl/ducks/actions'
import { detachUnitUserRoutine, reactivateUnitUserRoutine } from 'ducks/units/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { hideEmail, hidePhoneNumber } from 'utils/user'
import SearchIcon from '@mui/icons-material/Search'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { formatDate } from 'utils/date'
import { isEmpty } from 'ramda'
import { pathOr } from 'ramda'

const BREAKPOINT_MOBILE = 768

const normalizeRelation = (rt) => {
  if (!rt) return null
  if (typeof rt === 'string') return rt
  if (typeof rt === 'object') {
    return rt.value || rt.code || rt.key || rt.type || rt.name || rt.relation || null
  }
  return null
}

// Shared logic hook
const useRevokedRow = (user, unit, onRestore, onRemove) => {
  const [reactivateOpen, setReactivateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const dispatch = useDispatch()
  const currentBp = useSelector(getSelectedBp)

  const openReactivate = () => setReactivateOpen(true)
  const closeReactivate = () => setReactivateOpen(false)
  const openDelete = () => setDeleteOpen(true)
  const closeDelete = () => setDeleteOpen(false)

  const displayName = useMemo(() => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    return name || 'Anonymous user'
  }, [user.firstName, user.lastName])

  const roleKey = normalizeRelation(
    pathOr(null, ['pivot', 'relationType'], user) ||
    pathOr(null, ['pivot', 0, 'relationType'], user)
  )
  const roleLabel = (roleKey && USER_ROLES[roleKey]) || roleKey || '-'

  const revokedAt =
    pathOr(null, ['pivot', 'revokedAt'], user) ||
    pathOr(null, ['pivot', 0, 'revokedAt'], user)

  const onReactivate = () => {
    const relationId =
      pathOr(null, ['pivot', 'id'], user) ||
      pathOr(null, ['pivot', 0, 'id'], user)

    if (onRestore) {
      onRestore(user)
    } else if (unit) {
      dispatch(reactivateUnitUserRoutine({
        bpId: currentBp.id,
        unitId: unit.id,
        relationId
      }))
    } else {
      dispatch(restoreAccessToBpRoutine({
        bpId: currentBp.id,
        relationId
      }))
    }
    closeReactivate()
  }

  const onDelete = () => {
    const relationId =
      pathOr(null, ['pivot', 'id'], user) ||
      pathOr(null, ['pivot', 0, 'id'], user)

    if (onRemove) {
      onRemove(user)
    } else if (unit) {
      dispatch(detachUnitUserRoutine({
        bpId: currentBp.id,
        unitId: unit.id,
        relationId
      }))
    } else {
      dispatch(removeAccessToBpRoutine({
        bpId: currentBp.id,
        relationId
      }))
    }
    closeDelete()
  }

  return {
    displayName,
    roleLabel,
    revokedAt,
    reactivateOpen,
    deleteOpen,
    openReactivate,
    closeReactivate,
    openDelete,
    closeDelete,
    onReactivate,
    onDelete
  }
}

const RevokedRowMobile = ({ user, unit, canAccept, onRestore, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    displayName,
    roleLabel,
    revokedAt,
    reactivateOpen,
    deleteOpen,
    openReactivate,
    closeReactivate,
    openDelete,
    closeDelete,
    onReactivate,
    onDelete
  } = useRevokedRow(user, unit, onRestore, onRemove)

  const toggleExpand = (e) => {
    e?.stopPropagation?.()
    setIsExpanded(!isExpanded)
  }

  return (
    <>
      <MobileCard onClick={toggleExpand}>
        <MobileCardContent>
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            email={user.email}
            src={user.avatar}
            size={30}
          />
          <CardContent>
            <CardTop>
              <NameLabel>{displayName}</NameLabel>
            </CardTop>
            <EmailLabel>{user.email ? hideEmail(user.email) : '-'}</EmailLabel>
            {isExpanded && (
              <>
                <CardDetails>
                  <DetailRow>
                    <DetailLabel>Role:</DetailLabel>
                    <DetailValue>{roleLabel}</DetailValue>
                  </DetailRow>
                  {revokedAt && (
                    <DetailRow>
                      <DetailLabel>Revoked:</DetailLabel>
                      <DetailValue>{formatDate(revokedAt)}</DetailValue>
                    </DetailRow>
                  )}
                </CardDetails>
                {canAccept && (
                  <ActionButtons onClick={(e) => e.stopPropagation()}>
                    <ActionButton onClick={(e) => { e.stopPropagation(); openReactivate(); }}>
                      Reactivate
                    </ActionButton>
                    <ActionButton $red onClick={(e) => { e.stopPropagation(); openDelete(); }}>
                      Delete
                    </ActionButton>
                  </ActionButtons>
                )}
              </>
            )}
          </CardContent>
          <ChevronIcon $isExpanded={isExpanded}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </ChevronIcon>
        </MobileCardContent>
      </MobileCard>

      <Modal
        title="Reactivate access"
        open={reactivateOpen}
        onClose={closeReactivate}
        onCancel={closeReactivate}
        onSubmit={onReactivate}
        withCancel
        withSubmit
      >
        Do you really want to reactivate user&apos;s assignment?
      </Modal>

      <Modal
        title="Remove user"
        open={deleteOpen}
        onClose={closeDelete}
        onCancel={closeDelete}
        onSubmit={onDelete}
        withCancel
        withSubmit
      >
        Do you really want to remove user from the list?
      </Modal>
    </>
  )
}

const RevokedRowDesktop = ({ user, unit, canAccept, onRestore, onRemove }) => {
  const {
    displayName,
    roleLabel,
    revokedAt,
    reactivateOpen,
    deleteOpen,
    openReactivate,
    closeReactivate,
    openDelete,
    closeDelete,
    onReactivate,
    onDelete
  } = useRevokedRow(user, unit, onRestore, onRemove)

  return (
    <>
      <DesktopTableRow>
        <TableCell>
          <TableRowContent>
            <Avatar
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              src={user.avatar}
              size={30}
            />
            <NameLabel>{displayName}</NameLabel>
          </TableRowContent>
        </TableCell>
        <TableCell>{user.email ? hideEmail(user.email) : '-'}</TableCell>
        <TableCell>{roleLabel}</TableCell>
        <TableCell>{revokedAt ? formatDate(revokedAt) : '-'}</TableCell>
        {canAccept && (
          <TableCell>
            <ActionButtonsInline>
              <ActionButtonSmall onClick={openReactivate}>Reactivate</ActionButtonSmall>
              <ActionButtonSmall $red onClick={openDelete}>Delete</ActionButtonSmall>
            </ActionButtonsInline>
          </TableCell>
        )}
      </DesktopTableRow>

      <Modal
        title="Reactivate access"
        open={reactivateOpen}
        onClose={closeReactivate}
        onCancel={closeReactivate}
        onSubmit={onReactivate}
        withCancel
        withSubmit
      >
        Do you really want to reactivate user&apos;s assignment?
      </Modal>

      <Modal
        title="Remove user"
        open={deleteOpen}
        onClose={closeDelete}
        onCancel={closeDelete}
        onSubmit={onDelete}
        withCancel
        withSubmit
      >
        Do you really want to remove user from the list?
      </Modal>
    </>
  )
}

const RevokedSection = ({ users = [], unit, canAccept, onRestore, onRemove, onCountChange = () => {} }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users
    const q = searchQuery.toLowerCase()
    return users.filter(user => {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').toLowerCase()
      const email = (user.email || '').toLowerCase()
      const roleKey = normalizeRelation(
        pathOr(null, ['pivot', 'relationType'], user) ||
        pathOr(null, ['pivot', 0, 'relationType'], user)
      )
      const role = (USER_ROLES[roleKey] || roleKey || '').toString().toLowerCase()
      return name.includes(q) || email.includes(q) || role.includes(q)
    })
  }, [users, searchQuery])

  useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(filteredUsers.length)
    }
  }, [filteredUsers, onCountChange])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, itemsPerPage])

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage))
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filteredUsers.slice(start, end)
  }, [filteredUsers, currentPage, itemsPerPage])

  return (
    <Container>
      <SectionHeaderRow>
        <SectionTitle>Revoked users ({filteredUsers.length})</SectionTitle>
      </SectionHeaderRow>

      <SearchWrapper>
        <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
        <SearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchWrapper>

      <MobileListView>
        {!isEmpty(paginatedUsers) ? (
          paginatedUsers.map((user) => (
            <RevokedRowMobile
              key={user.id}
              user={user}
              unit={unit}
              canAccept={canAccept}
              onRestore={onRestore}
              onRemove={onRemove}
            />
          ))
        ) : (
          <EmptyStateText>No revoked users found</EmptyStateText>
        )}
      </MobileListView>

      <DesktopTableView>
        <StyledTable>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Revoked</th>
              {canAccept && <th style={{ width: 150 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {!isEmpty(paginatedUsers) ? (
              paginatedUsers.map((user) => (
                <RevokedRowDesktop
                  key={user.id}
                  user={user}
                  unit={unit}
                  canAccept={canAccept}
                  onRestore={onRestore}
                  onRemove={onRemove}
                />
              ))
            ) : (
              <tr>
                <td colSpan={canAccept ? 5 : 4} style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                  {searchQuery ? 'No revoked users match your search' : 'No revoked users yet'}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </DesktopTableView>

      {filteredUsers.length > 0 && (
        <PaginationWrapper>
          <Pagination>
            <PaginationInfo>
              Showing {(currentPage - 1) * itemsPerPage + 1}
              -{Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} revoked users
            </PaginationInfo>

            <PaginationControls>
              <DesktopOnlyWrap>
                <PageSizeWrap>
                  <PageSizeLabel>Rows:</PageSizeLabel>
                  <SearchableSelect
                    value={itemsPerPage}
                    onChange={(v) => setItemsPerPage(v)}
                    options={[5, 10, 15, 25, 50].map((size) => ({ value: size, label: String(size) }))}
                    ariaLabel="Rows per page"
                  />
                </PageSizeWrap>
              </DesktopOnlyWrap>

              <IconPaginationButton
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                title="Previous page"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconPaginationButton>

              <DesktopOnlyWrap>
                <PageNumbers>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1
                    if (
                      totalPages <= 15 ||
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <PageNumber
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          $active={currentPage === page}
                        >
                          {page}
                        </PageNumber>
                      )
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <Ellipsis key={page}>…</Ellipsis>
                    }
                    return null
                  })}
                </PageNumbers>
              </DesktopOnlyWrap>

              <IconPaginationButton
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                title="Next page"
              >
                <ChevronRightIcon fontSize="small" />
              </IconPaginationButton>
            </PaginationControls>
          </Pagination>
        </PaginationWrapper>
      )}
    </Container>
  )
}

export default RevokedSection

const Container = styled.div`
  width: 100%;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0;
  }
`

const SectionHeaderRow = styled.div`
  margin-bottom: 12px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
  }
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:focus-within {
    background: white;
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.1);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin: 0 16px 20px 16px;
  }
`

const SearchInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 15px;
  color: #1e293b;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`

const MobileListView = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    flex-direction: column;
  }
`

const MobileCard = styled.div`
  background: white;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px;
  cursor: pointer;
  transition: background 0.1s;

  &:active {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`

const MobileCardContent = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  color: #94a3b8;
  transition: transform 0.2s;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const NameLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`

const EmailLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
`

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`

const DetailLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`

const DetailValue = styled.span`
  color: #1e293b;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }
`

const DesktopTableView = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.5px;
  }

  tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.1s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`

const DesktopTableRow = styled.tr``

const TableCell = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: #1e293b;
`

const TableRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ActionButtonsInline = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const ActionButtonSmall = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`

const EmptyStateText = styled.div`
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`

const PaginationWrapper = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
    margin-top: 16px;
    padding-top: 16px;
  }
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
  flex: 1;
  text-align: center;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 12px;
    order: -1;
    width: 100%;
  }
`

const DesktopOnlyWrap = styled.div`
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`

const PageSizeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
  }
`

const PageSizeLabel = styled.span`
  color: #666;
  font-size: 13px;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 640px) {
    gap: 4px;
  }
`

const PageNumber = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ $active }) => ($active ? '#d14724' : '#e0e0e0')};
  background: ${({ $active }) => ($active ? '#d14724' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#333')};
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${({ $active }) => ($active ? '#d14724' : '#f5f5f5')};
    border-color: #d14724;
    color: ${({ $active }) => ($active ? 'white' : '#d14724')};
  }

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`

const Ellipsis = styled.span`
  color: #999;
  padding: 0 5px;

  @media (max-width: 640px) {
    padding: 0 2px;
  }
`

const IconPaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #d14724;
    color: #d14724;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 32px;
    height: 28px;
  }
`
